/* mr.js — Mirel interactions (Lumen Terrain Light layer).
   GSAP (+ScrollTrigger) is loaded as classic deferred scripts before this file; three.js is imported lazily only when the
   hero scrolls into view, WebGL works and the visitor has not asked for reduced motion.
   Everything degrades: no GSAP -> content simply shows; no WebGL -> the static photo card stays.
   Smoothness rules (Ninos 2026-10-26): reveals are CSS transitions flipped by IntersectionObserver, nothing is scrubbed to
   scroll, no backdrop blur, the WebGL loop pauses off-screen, pixel ratio capped at 1.5, animation runs on elapsed time. */
(function () {
  'use strict';
  var doc = document, root = doc.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var g = window.gsap, ST = window.ScrollTrigger;
  var hasGsap = !!(g && ST) && !reduced;
  if (hasGsap) { g.registerPlugin(ST); }

  /* ---------------- Reveals v2 ---------------- */
  function markReveals(scope) {
    var sel = [
      '.pp-section .pp-eyebrow', '.pp-section .pp-h2', '.pp-section .pp-sub', '.pp-section .pp-text', '.pp-section .pp-cta',
      '.vc-card', '.pp-gb', '.pp-faq__item', '.pp-guarantee__card', '.pp-media', '.vc-results__row', '.vc-results__kicker',
      '.vc-results__text', '.vc-results__caption', '.pp-pct', '.testimonial-card', '.collection .card-wrapper',
      '.collection-title', '.comparison-table-container', '.content-and-comparison-table .content-container',
      '.mr-how__media', '.mr-step', '.mr-how__cta', '.mr-how__note',
      '.newsletter__wrapper > *'
    ].join(',');
    var nodes = (scope || doc).querySelectorAll(sel);
    nodes.forEach(function (n) {
      if (n.closest('.mr-hero')) return;
      if (!n.hasAttribute('data-mr-reveal')) n.setAttribute('data-mr-reveal', '');
    });
    return nodes;
  }
  function runReveals() {
    if (reduced || !('IntersectionObserver' in window)) return;
    var vh = window.innerHeight || 800, list = [];
    markReveals().forEach(function (n) {
      if (n.getBoundingClientRect().top > vh) list.push(n); else n.removeAttribute('data-mr-reveal');
    });
    if (!list.length) return;
    root.classList.add('mr-reveal');
    var io = new IntersectionObserver(function (entries) {
      var k = 0;
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var n = e.target; io.unobserve(n);
        n.style.transitionDelay = Math.min(k++, 8) * 60 + 'ms';
        n.addEventListener('transitionend', function done() { n.classList.add('mr-done'); n.style.transitionDelay = ''; n.removeEventListener('transitionend', done); });
        n.classList.add('mr-in');
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    list.forEach(function (n) { io.observe(n); });
    setTimeout(function () { list.forEach(function (n) { if (!n.classList.contains('mr-in')) { n.classList.add('mr-in', 'mr-done'); } }); }, 8000);
  }

  /* ---------------- Percent rings count up ---------------- */
  function runRings() {
    if (!hasGsap) return;
    doc.querySelectorAll('.pp-pct__ring').forEach(function (ring) {
      var span = ring.querySelector('span'); if (!span) return;
      var target = parseFloat(ring.style.getPropertyValue('--pp-value')) || parseFloat(span.textContent) || 0;
      var obj = { v: 0 };
      ring.style.setProperty('--pp-value', '0'); span.textContent = '0%';
      ST.create({
        trigger: ring, start: 'top 90%', once: true,
        onEnter: function () {
          g.to(obj, { v: target, duration: 1.6, ease: 'power2.out', onUpdate: function () {
            ring.style.setProperty('--pp-value', obj.v.toFixed(1)); span.textContent = Math.round(obj.v) + '%';
          } });
        }
      });
    });
  }

  /* ---------------- Tilt + magnetic (fine pointers only) ---------------- */
  function runTilt() {
    if (!hasGsap || !finePointer) return;
    doc.querySelectorAll('.collection .card-wrapper .card, .vc-cards--tiles .vc-card, .pp-gb').forEach(function (el) {
      el.classList.add('mr-tilt');
      var rx = g.quickTo(el, 'rotationX', { duration: 0.5, ease: 'power3' });
      var ry = g.quickTo(el, 'rotationY', { duration: 0.5, ease: 'power3' });
      g.set(el, { transformPerspective: 900 });
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        rx(-py * 5); ry(px * 7);
      });
      el.addEventListener('pointerleave', function () { rx(0); ry(0); });
    });
    doc.querySelectorAll('.button--primary, .mr-hero__actions .button, .mr-how__cta .button').forEach(function (b) {
      var x = g.quickTo(b, 'x', { duration: 0.4, ease: 'power3' }), y = g.quickTo(b, 'y', { duration: 0.4, ease: 'power3' });
      b.addEventListener('pointermove', function (e) {
        var r = b.getBoundingClientRect();
        x(((e.clientX - r.left) / r.width - 0.5) * 8); y(((e.clientY - r.top) / r.height - 0.5) * 6);
      });
      b.addEventListener('pointerleave', function () { x(0); y(0); });
    });
  }

  /* ---------------- How it works stepper (time-based, hover/tap picks) ---------------- */
  function runHow() {
    doc.querySelectorAll('[data-mr-how]').forEach(function (sec) {
      if (sec.hasAttribute('data-mr-ready')) return;
      sec.setAttribute('data-mr-ready', '');
      var steps = Array.prototype.slice.call(sec.querySelectorAll('[data-mr-step]'));
      if (steps.length < 2) return;
      var pulse = sec.querySelector('[data-mr-pulse]');
      var interval = parseInt(sec.getAttribute('data-interval'), 10) || 2800;
      var idx = 0, timer = null, hold = null, visible = false, tween = null;
      function fillBar(step) {
        var bar = step.querySelector('[data-mr-bar]'); if (!bar) return;
        if (hasGsap) { if (tween) tween.kill(); g.set(bar, { scaleX: 0 }); tween = g.to(bar, { scaleX: 1, duration: interval / 1000, ease: 'none' }); }
        else { bar.style.transition = 'none'; bar.style.transform = 'scaleX(0)'; void bar.offsetWidth; bar.style.transition = 'transform ' + interval + 'ms linear'; bar.style.transform = 'scaleX(1)'; }
      }
      function setStep(i) {
        idx = (i + steps.length) % steps.length;
        steps.forEach(function (s, k) {
          var on = k === idx; s.classList.toggle('is-active', on);
          var bar = s.querySelector('[data-mr-bar]'); if (bar && !on) { if (hasGsap) g.set(bar, { scaleX: 0 }); else { bar.style.transition = 'none'; bar.style.transform = 'scaleX(0)'; } }
        });
        fillBar(steps[idx]);
        if (pulse && idx === steps.length - 1 && hasGsap) {
          g.fromTo(pulse, { opacity: 0.9, scale: 0.4 }, { opacity: 0, scale: 2.2, duration: 1.4, ease: 'power2.out', overwrite: true });
        }
      }
      function start() { stop(); if (reduced) return; timer = setInterval(function () { setStep(idx + 1); }, interval); }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      steps.forEach(function (s, k) {
        s.addEventListener('pointerenter', function () { if (!finePointer) return; setStep(k); stop(); });
        s.addEventListener('pointerleave', function () { if (!finePointer) return; if (visible) start(); });
        s.addEventListener('click', function () { setStep(k); stop(); if (hold) clearTimeout(hold); hold = setTimeout(function () { if (visible) start(); }, 6000); });
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (en) {
          visible = en[0].isIntersecting;
          if (visible) { setStep(idx); start(); } else stop();
        }, { threshold: 0.3 }).observe(sec);
      } else { setStep(0); start(); }
      doc.addEventListener('visibilitychange', function () { if (doc.hidden) stop(); else if (visible) start(); });
    });
  }

  /* ---------------- Hero copy ---------------- */
  function runHeroCopy(hero) {
    var h = hero.querySelector('.mr-hero__h');
    if (h && !h.querySelector('.mr-w')) {
      h.innerHTML = h.innerHTML.replace(/(<em>.*?<\/em>|[^\s<]+)(\s*)/g, function (_, w, sp) { return '<span class="mr-w">' + w + (sp ? ' ' : '') + '</span>'; });
    }
    if (!hasGsap) return;
    var tl = g.timeline({ defaults: { ease: 'power3.out' } });
    tl.from(hero.querySelectorAll('.pp-eyebrow'), { y: 14, opacity: 0, duration: 0.7 }, 0.05)
      .from(hero.querySelectorAll('.mr-w'), { yPercent: 60, opacity: 0, duration: 0.9, stagger: 0.07 }, 0.1)
      .from(hero.querySelectorAll('.mr-hero__lead'), { y: 16, opacity: 0, duration: 0.8 }, 0.5)
      .from(hero.querySelectorAll('.mr-hero__actions .button'), { y: 12, opacity: 0, duration: 0.6, stagger: 0.1 }, 0.65)
      .from(hero.querySelectorAll('.mr-hero__metric'), { y: 16, opacity: 0, duration: 0.7, stagger: 0.08 }, 0.8)
      .from(hero.querySelectorAll('.mr-hero__chip, .mr-hero__tag, .mr-hero__hint, .mr-hero__quiet'), { y: 10, opacity: 0, duration: 0.6 }, 0.9);
    // Insurance: nothing stays at opacity 0 if a tween is interrupted (theme editor, tab switch)
    setTimeout(function () { hero.querySelectorAll('.mr-w, .pp-eyebrow, .mr-hero__lead, .button, .mr-hero__metric, .mr-hero__chip, .mr-hero__tag, .mr-hero__hint, .mr-hero__quiet').forEach(function (n) { n.style.opacity = ''; n.style.transform = ''; }); }, 2600);
  }

  /* ---------------- Hero: three.js calm field ---------------- */
  function webglOk() {
    try { var c = doc.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); } catch (e) { return false; }
  }
  // Shopify minifies theme JS and rewrites a dynamic import() into require(), so the ES module is pulled in by an
  // inline <script type="module"> instead and handed over on window.
  function loadThree(src) {
    return new Promise(function (resolve, reject) {
      if (window.__mrThree) return resolve(window.__mrThree);
      window.addEventListener('mr:three', function () { resolve(window.__mrThree); }, { once: true });
      var s = doc.createElement('script'); s.type = 'module';
      s.textContent = "import * as T from '" + src + "'; window.__mrThree = T; window.dispatchEvent(new Event('mr:three'));";
      doc.head.appendChild(s);
      setTimeout(function () { if (!window.__mrThree) reject(new Error('three.js did not load')); }, 15000);
    });
  }
  function initHero3D(hero) {
    var canvas = hero.querySelector('.mr-hero__canvas');
    if (!canvas || reduced || !webglOk()) return;
    var src = hero.getAttribute('data-three') || 'https://cdnjs.cloudflare.com/ajax/libs/three.js/0.160.0/three.module.min.js';
    loadThree(src).then(function (THREE) { buildHero(THREE, hero, canvas); }).catch(function (err) { if (window.console) console.warn('mr hero 3d skipped', err); });
  }

  // Terrain: a mist-blue plane whose height is a sum of sines. uAmp 1 = restless (snoring night), 0 = calm.
  // The fragment paints flowing contour lines (fract of the height) in signal blue over a white/ice gradient,
  // plus a pulse ring that expands from the device when the visitor presses Quiet.
  var FIELD_VERT = [
    'uniform float uTime; uniform float uAmp;',
    'varying vec3 vNormal; varying vec3 vPos; varying vec2 vUv; varying float vH;',
    'float wave(vec2 p, float t, float a){',
    '  float calm = 0.10*sin(p.x*0.9 + t*0.35) + 0.07*sin(p.y*1.3 - t*0.28);',
    '  float rough = 0.22*sin(p.x*2.3 + t*1.9) + 0.16*sin(p.y*3.1 - t*1.6) + 0.10*sin((p.x+p.y)*4.2 + t*2.8) + 0.06*sin(p.x*7.0 - t*4.0);',
    '  return calm + rough*a;',
    '}',
    'void main(){',
    '  vUv = uv;',
    '  vec3 p = position;',
    '  float e = 0.02;',
    '  float z = wave(p.xy, uTime, uAmp);',
    '  float zx = wave(p.xy + vec2(e,0.0), uTime, uAmp);',
    '  float zy = wave(p.xy + vec2(0.0,e), uTime, uAmp);',
    '  p.z += z; vH = z;',
    '  vec3 n = normalize(vec3(-(zx-z)/e, -(zy-z)/e, 1.0));',
    '  vNormal = normalize(normalMatrix * n);',
    '  vec4 mv = modelViewMatrix * vec4(p, 1.0);',
    '  vPos = mv.xyz;',
    '  gl_Position = projectionMatrix * mv;',
    '}'
  ].join('\n');
  var FIELD_FRAG = [
    'uniform vec3 uBase; uniform vec3 uShade; uniform vec3 uLine; uniform float uOpacity; uniform float uAmp; uniform float uPulse; uniform vec2 uPulseAt;',
    'varying vec3 vNormal; varying vec3 vPos; varying vec2 vUv; varying float vH;',
    'void main(){',
    '  vec3 n = normalize(vNormal);',
    '  vec3 l = normalize(vec3(-0.4, 0.7, 1.0));',
    '  vec3 v = normalize(-vPos);',
    '  float diff = clamp(dot(n, l), 0.0, 1.0);',
    '  float fres = pow(1.0 - clamp(dot(n, v), 0.0, 1.0), 2.0);',
    '  vec3 col = mix(uShade, uBase, 0.45 + 0.55*diff);',
    '  float lines = 4.5 + 3.0*uAmp;',
    '  float f = fract(vH*lines + vUv.y*2.0);',
    '  float d = min(f, 1.0-f);',
    '  float aa = fwidth(vH*lines)*1.6 + 0.02;',
    '  float line = 1.0 - smoothstep(0.0, aa, d);',
    '  col = mix(col, uLine, line*(0.14 + 0.2*uAmp));',
    '  col += uLine * fres * 0.12;',
    '  float r = distance(vUv, uPulseAt);',
    '  float ring = 1.0 - smoothstep(0.0, 0.05, abs(r - uPulse*0.75));',
    '  col = mix(col, uLine, ring * (1.0 - uPulse) * 0.6 * step(0.001, uPulse));',
    '  float edge = smoothstep(0.0, 0.22, vUv.x) * smoothstep(0.0, 0.22, 1.0-vUv.x) * smoothstep(0.0, 0.2, vUv.y) * smoothstep(0.0, 0.24, 1.0-vUv.y);',
    '  gl_FragColor = vec4(col, uOpacity * edge);',
    '}'
  ].join('\n');

  function buildHero(THREE, hero, canvas) {
    var stage = hero.querySelector('.mr-hero__stage');
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var texUrl = hero.getAttribute('data-texture');
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
    camera.position.set(0, 1.4, 7.8); camera.lookAt(0, 0.1, 0);

    // Field
    var fieldMat = new THREE.ShaderMaterial({
      vertexShader: FIELD_VERT, fragmentShader: FIELD_FRAG, transparent: true, depthWrite: false, side: THREE.DoubleSide,
      uniforms: {
        uTime: { value: 0 }, uAmp: { value: 1 }, uOpacity: { value: 0 }, uPulse: { value: 0 }, uPulseAt: { value: new THREE.Vector2(0.5, 0.56) },
        uBase: { value: new THREE.Color(0xf6faff) }, uShade: { value: new THREE.Color(0xdbe9ff) }, uLine: { value: new THREE.Color(0x2f7dff) }
      }
    });
    var field = new THREE.Mesh(new THREE.PlaneGeometry(7.2, 6.6, 96, 88), fieldMat);
    field.position.set(0, -0.6, -1.4); field.rotation.set(-0.95, 0, 0); scene.add(field);

    // Product card: white mat + photo, floating above the field
    var card = new THREE.Group(); card.position.set(0, 0.35, 1.0); scene.add(card);
    var matWhite = new THREE.MeshBasicMaterial({ color: 0xffffff });
    var matLine = new THREE.MeshBasicMaterial({ color: 0xbbd9ff });
    var CW = 2.7, CH = 2.7;
    var frame2 = new THREE.Mesh(new THREE.PlaneGeometry(CW + 0.04, CH + 0.04), matLine); frame2.position.z = -0.012; card.add(frame2);
    var mat = new THREE.Mesh(new THREE.PlaneGeometry(CW + 0.02, CH + 0.02), matWhite); mat.position.z = -0.006; card.add(mat);
    var back = new THREE.Mesh(new THREE.PlaneGeometry(CW + 0.04, CH + 0.04), matWhite); back.rotation.y = Math.PI; back.position.z = -0.014; card.add(back);
    var photoMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0 });
    var photo = new THREE.Mesh(new THREE.PlaneGeometry(CW - 0.14, CH - 0.14), photoMat); card.add(photo);
    // soft shadow disc under the card
    var sc0 = doc.createElement('canvas'); sc0.width = sc0.height = 128; var c0 = sc0.getContext('2d');
    var g0 = c0.createRadialGradient(64, 64, 4, 64, 64, 60); g0.addColorStop(0, 'rgba(41,92,160,0.35)'); g0.addColorStop(1, 'rgba(41,92,160,0)');
    c0.fillStyle = g0; c0.fillRect(0, 0, 128, 128);
    var shadow = new THREE.Mesh(new THREE.PlaneGeometry(3.6, 1.6), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(sc0), transparent: true, depthWrite: false }));
    shadow.rotation.x = -Math.PI / 2; shadow.position.set(0, -1.05, 0.9); scene.add(shadow);
    if (texUrl) {
      var loader = new THREE.TextureLoader(); loader.setCrossOrigin('anonymous');
      loader.load(texUrl, function (t) {
        t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
        photoMat.map = t; photoMat.needsUpdate = true;
        if (hasGsap) g.to(photoMat, { opacity: 1, duration: 0.8 }); else photoMat.opacity = 1;
      });
    }

    // Mist dust
    var N = isMobile ? 160 : 360, pos = new Float32Array(N * 3), vel = new Float32Array(N * 2);
    var SX = 8, SY = 6, SZ = 4;
    for (var i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * SX; pos[i * 3 + 1] = (Math.random() - 0.5) * SY; pos[i * 3 + 2] = (Math.random() - 0.5) * SZ - 0.5;
      vel[i * 2] = 0.08 + Math.random() * 0.2; vel[i * 2 + 1] = Math.random() * Math.PI * 2;
    }
    var dustGeo = new THREE.BufferGeometry(); dustGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    var sc = doc.createElement('canvas'); sc.width = sc.height = 64; var sx2 = sc.getContext('2d');
    var grd = sx2.createRadialGradient(32, 32, 1, 32, 32, 14); grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.45, 'rgba(106,167,255,0.85)'); grd.addColorStop(1, 'rgba(106,167,255,0)');
    sx2.fillStyle = grd; sx2.fillRect(0, 0, 64, 64);
    var dustMat = new THREE.PointsMaterial({ size: isMobile ? 0.08 : 0.1, map: new THREE.CanvasTexture(sc), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0.75, sizeAttenuation: true });
    scene.add(new THREE.Points(dustGeo, dustMat));

    // State (rates are per second; the loop steps on elapsed time)
    var st = { rotY: 0, dragY: 0, px: 0, py: 0, running: true, visible: true, t: 0, amp: 1, quiet: false, pulse: 0, pulseOn: false };
    card.scale.setScalar(0.001);
    hero.classList.add('mr-hero--3d');

    function resize() {
      var w = stage.clientWidth || 600, h = stage.clientHeight || w;
      renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
    }
    resize();
    if (window.ResizeObserver) new ResizeObserver(resize).observe(stage); else window.addEventListener('resize', resize);

    // Quiet toggle
    var quietBtn = hero.querySelector('[data-mr-quiet]');
    function setQuiet(on) {
      st.quiet = on;
      if (quietBtn) { quietBtn.classList.toggle('is-on', on); quietBtn.setAttribute('aria-pressed', on ? 'true' : 'false'); }
      if (on) { st.pulse = 0; st.pulseOn = true; }
      if (hasGsap) { g.to(st, { amp: on ? 0.06 : 1, duration: on ? 1.6 : 1.1, ease: on ? 'power3.out' : 'power2.inOut' }); }
      else st.amp = on ? 0.06 : 1;
      dustMat.opacity = on ? 0.45 : 0.75;
    }
    if (quietBtn) quietBtn.addEventListener('click', function () { setQuiet(!st.quiet); });

    // Intro
    if (hasGsap) {
      var sObj = { s: 0.001 };
      g.timeline()
        .to(fieldMat.uniforms.uOpacity, { value: 0.95, duration: 1.4, ease: 'power2.out' }, 0)
        .to(sObj, { s: 1, duration: 1.1, ease: 'back.out(1.4)', onUpdate: function () { card.scale.setScalar(sObj.s); } }, 0.25);
    } else { card.scale.setScalar(1); fieldMat.uniforms.uOpacity.value = 0.95; }

    // Pointer: parallax, drag to tilt, tap toggles quiet
    var dragging = false, moved = 0, lastX = 0;
    window.addEventListener('pointermove', function (e) {
      st.px = (e.clientX / window.innerWidth - 0.5) * 2; st.py = (e.clientY / window.innerHeight - 0.5) * 2;
    }, { passive: true });
    canvas.addEventListener('pointerdown', function (e) { dragging = true; moved = 0; lastX = e.clientX; if (canvas.setPointerCapture) canvas.setPointerCapture(e.pointerId); });
    canvas.addEventListener('pointermove', function (e) {
      if (!dragging) return; var dx = e.clientX - lastX; lastX = e.clientX; moved += Math.abs(dx); st.dragY += dx * 0.01;
    });
    function up() { if (!dragging) return; dragging = false; if (moved < 6) setQuiet(!st.quiet); }
    canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);

    // Pause when off-screen or tab hidden
    if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { st.visible = en[0].isIntersecting; }, { threshold: 0.25 }).observe(stage);
    doc.addEventListener('visibilitychange', function () { st.running = !doc.hidden; });

    var clock = new THREE.Clock();
    function loop() {
      requestAnimationFrame(loop);
      if (!st.running || !st.visible) return;
      var dt = Math.min(clock.getDelta(), 0.05); st.t += dt;
      fieldMat.uniforms.uTime.value = st.t;
      fieldMat.uniforms.uAmp.value = st.amp;
      if (st.pulseOn) { st.pulse += dt * 0.55; if (st.pulse >= 1) { st.pulse = 0; st.pulseOn = false; } }
      fieldMat.uniforms.uPulse.value = st.pulse;
      var p = dustGeo.attributes.position.array, speed = 0.35 + 0.65 * st.amp;
      for (var i = 0; i < N; i++) {
        var yi = i * 3 + 1; p[yi] += vel[i * 2] * speed * dt; p[i * 3] += Math.sin(st.t * 0.6 + vel[i * 2 + 1]) * 0.12 * speed * dt;
        if (p[yi] > SY / 2) { p[yi] = -SY / 2; p[i * 3] = (Math.random() - 0.5) * SX; }
      }
      dustGeo.attributes.position.needsUpdate = true;
      // Card: settle toward the drag angle (snapped to a face), pointer tilt, idle float - slower and smaller when quiet
      var snap = Math.round(st.dragY / Math.PI) * Math.PI;
      if (!dragging) st.dragY += (snap - st.dragY) * Math.min(1, 3.6 * dt);
      st.rotY += (st.dragY + st.px * 0.18 - st.rotY) * Math.min(1, 6 * dt);
      card.rotation.y = st.rotY;
      var jitter = (1 - st.amp) * 0.0 + st.amp * 0.012;
      card.rotation.x = st.py * -0.05 + Math.sin(st.t * (0.9 + 1.4 * st.amp)) * jitter;
      card.position.y = 0.35 + Math.sin(st.t * (1.0 + 0.8 * st.amp)) * (0.03 + 0.05 * st.amp);
      shadow.scale.setScalar(1 - 0.06 * Math.sin(st.t * 1.0));
      camera.position.x += ((st.px * 0.35) - camera.position.x) * Math.min(1, 3 * dt);
      camera.position.y += ((1.4 + st.py * -0.2) - camera.position.y) * Math.min(1, 3 * dt);
      camera.lookAt(0, 0.1, 0);
      renderer.render(scene, camera);
    }
    loop();
  }

  function initHero() {
    doc.querySelectorAll('[data-mr-hero]').forEach(function (hero) {
      if (hero.hasAttribute('data-mr-ready')) return;
      hero.setAttribute('data-mr-ready', '');
      runHeroCopy(hero);
      if (reduced) return;
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); initHero3D(hero); } }, { rootMargin: '200px' });
        io.observe(hero);
      } else initHero3D(hero);
    });
  }

  function boot() {
    initHero(); runHow(); runReveals(); runRings(); runTilt();
    if (hasGsap) { setTimeout(function () { ST.refresh(); }, 600); window.addEventListener('load', function () { ST.refresh(); }); if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(function () { ST.refresh(); }); }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
  doc.addEventListener('shopify:section:load', function () { runReveals(); initHero(); runHow(); if (hasGsap) ST.refresh(); });
})();
